/**
 * Recorder TanStack Query hooks.
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { recorderKeys } from "@/queries/keys";
import {
  listRecorders,
  getRecorderDetails,
  getRecorderRecordings,
  getMediaRecording,
  getRecorderStatistics,
  getRecorderSubmissions,
  fetchLiveTranscriptionToken,
  type RecorderListFilters,
  type MediaRecording,
} from "./api";

/** Fetch paginated recorder list with filters. */
export function useRecorderList(
  filters: RecorderListFilters = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: recorderKeys.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const res = await listRecorders(filters);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch recorder list");
      return res.data!;
    },
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Fetch survey/recording details for a media that came from a recorder.
 * Resolves to null when the media has no recording (endpoint returns `[]`).
 */
export function useMediaRecording(mediaId: string | undefined) {
  return useQuery({
    queryKey: recorderKeys.mediaRecording(mediaId!),
    queryFn: async (): Promise<MediaRecording | null> => {
      const res = await getMediaRecording(mediaId!);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch recording details");
      const data = res.data;
      // The endpoint returns [] when the media isn't from a recorder; also treat
      // an empty/malformed object (no recorderId) as "no recording".
      if (!data || Array.isArray(data) || !data.recorderId) return null;
      return data;
    },
    enabled: !!mediaId,
  });
}

/** Fetch a single recorder's details. */
export function useRecorderDetail(recorderId: string | undefined) {
  return useQuery({
    queryKey: recorderKeys.detail(recorderId!),
    queryFn: async () => {
      const res = await getRecorderDetails(recorderId!);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch recorder details");
      return res.data!;
    },
    enabled: !!recorderId,
  });
}

/** Fetch recordings for a specific recorder. */
export function useRecorderRecordings(
  recorderId: string | undefined,
  page: number = 0,
  pageSize: number = 25,
  filters: { filterName?: string; sortBy?: string; sortDir?: string } = {}
) {
  return useQuery({
    queryKey: recorderKeys.recordings(recorderId!, {
      page,
      pageSize,
      ...filters,
    }),
    queryFn: async () => {
      const res = await getRecorderRecordings(
        recorderId!,
        page,
        pageSize,
        filters
      );
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch recordings");
      return res.data!;
    },
    enabled: !!recorderId,
    placeholderData: keepPreviousData,
  });
}

/** Fetch recorder statistics. */
export function useRecorderStatistics(recorderId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: recorderKeys.statistics(recorderId!),
    queryFn: async () => {
      const res = await getRecorderStatistics(recorderId!);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch recorder statistics");
      return res.data!;
    },
    enabled: !!recorderId && enabled,
  });
}

/** Fetch submission summary. */
export function useRecorderSubmissions(
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: recorderKeys.submissions(startDate, endDate),
    queryFn: async () => {
      const res = await getRecorderSubmissions(startDate, endDate);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch submissions");
      return res.data!;
    },
  });
}

/**
 * Resolve a short-lived live transcription handshake token. Cached for the session
 * and refetched 4 hours in, comfortably ahead of the 5-hour server-side expiry.
 */
export function useLiveTranscriptionToken(enabled: boolean) {
  return useQuery({
    queryKey: recorderKeys.liveTranscriptionToken(),
    enabled,
    staleTime: 4 * 60 * 60 * 1000,
    gcTime: Infinity,
    retry: 1,
    queryFn: async () => {
      const res = await fetchLiveTranscriptionToken();
      if (!res.success || !res.data?.token)
        throw new Error(res.error ?? "Could not prepare live transcription");
      return res.data.token;
    },
  });
}
